import type { Theme } from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { baeDarkTheme, baeLightTheme } from "./baeTheme";

export type ColourSchemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "commerceos.colour-scheme";

function readStoredPreference(): ColourSchemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // Private windows and locked-down browsers throw on access. Defaulting is correct.
  }
  return "system";
}

/**
 * Resolves the active theme from the user's choice and the operating system.
 *
 * "System" is the default and a real third state, not a synonym for light: a warehouse
 * tablet on a dark OS should open dark without anyone configuring it.
 */
export function useColourScheme() {
  const [preference, setPreference] = useState<ColourSchemePreference>(readStoredPreference);
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) {
      return;
    }

    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const choose = useCallback((next: ColourSchemePreference) => {
    setPreference(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A remembered preference is a convenience, not a requirement.
    }
  }, []);

  const isDark = preference === "dark" || (preference === "system" && systemPrefersDark);
  const theme: Theme = useMemo(() => (isDark ? baeDarkTheme : baeLightTheme), [isDark]);

  useEffect(() => {
    document.documentElement.style.colorScheme = isDark ? "dark" : "light";
  }, [isDark]);

  return { theme, isDark, preference, choose };
}
